<template>
  <div>
    <div v-if="isLoading" class="text-center py-5 text-secondary">
      {{ $t("loadingData") }}
    </div>

    <template v-else-if="subscriptions">
      <h5 class="mb-3">{{ $t("registerServiceInfo") }}</h5>

      <template v-if="activeSubscription">
        <div class="px-4 py-3 my-4 border rounded">
          <b-row class="align-items-center mb-3">
            <b-col md="4">
              <h5>{{ activeSubscriptionPlan.name }}</h5>
              <div>{{ $t("displayActiveSubPrice", {
                price: formatCurrencyAmount(activeSubscriptionPlan.price),
                months: activeSubscriptionPlan.numberOfDaysUsage
              }) }}</div>
            </b-col>
            <b-col md="4">
              <div>
                <b-icon icon="camera-video" class="mr-2"/>
                {{ $t("displayActiveSubMeetingCount", { count: activeSubscriptionPlan.numberOfMeetingsPerMonth }) }}
              </div>
              <div>
                <b-icon icon="blank" class="mr-2"/>
                {{ $t("displayActiveSubConcurrentMeetings", { count: activeSubscription.numberOfConcurrentMeetings ? activeSubscription.numberOfConcurrentMeetings : activeSubscriptionPlan.numberOfConcurrentMeetings }) }}
              </div>
            </b-col>
            <b-col md="4">
              <div>
                <b-icon icon="people" class="mr-2"/>
                {{ $t("displayActiveSubMaxParticipants", { count: activeSubscriptionPlan.maximumNumberOfParticipantsInMeeting }) }}
              </div>
              <div>
                <b-icon icon="clock" class="mr-2"/>
                {{ $t("displayActiveSubMaxMeetingLength", { count: activeSubscriptionPlan.maximumLengthOfOneMeeting }) }}
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="4">
              <span class="text-secondary">{{ $t("subCreatedDate") }}:</span> {{ localDateFormat(activeSubscription.createdTime) }}
            </b-col>
            <b-col md="4">
              <span class="text-secondary">{{ $t("startDate") }}: </span> {{ localDateFormat(activeSubscription.startTime) }}
            </b-col>
            <b-col md="4">
              <span class="text-secondary">{{ $t("expDate") }}: </span> {{ localDateFormat(activeSubscription.endTime) }}
            </b-col>
          </b-row>
        </div>

        <div class="pb-4 mb-4 border-bottom" v-if="isManager() || isUserAdmin()">
          <b-button variant="outline-primary" class="mr-3" v-b-modal:extend-subscription-modal>
            {{ $t("renewService") }}
          </b-button>
          <b-button variant="outline-success" class="mr-3" v-b-modal:upgrade-subscription-modal>
            {{ $t("upgradeService") }}
          </b-button>
          <b-button variant="outline-danger" class="mr-3"  @click="onclickCancelService">
            {{ $t("cancelService") }}
          </b-button>
          <b-button variant="outline-secondary" @click="onClickSendRemindEmail">
            {{ $t("sendServiceRenewReminderEmail") }}
          </b-button>
        </div>
      </template>

      <div v-else class="pb-4 mb-4 border-bottom">
        <div class="text-secondary">
          <div class="mb-2">{{ $t("noRegisteredService") }}</div>
          <b-button v-if="isManager()" variant="outline-primary" v-b-modal:register-subscription-modal>{{ $t("registerService") }}</b-button>
        </div>
      </div>

      <b-form-radio-group class="mb-3" buttons button-variant="outline-primary"
        v-model="historyTableViewMode" :options="historyTableViewModeOptions" />

      <b-table v-if="historyTableViewMode === 'subscription'" :fields="tableFields" :items="subscriptionsHistory" show-empty>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
      </b-table>

      <b-table v-if="historyTableViewMode === 'storage'" :fields="storageHistoryTableFields" :items="storageHistory" show-empty>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
      </b-table>

    </template>

    <b-modal id="extend-subscription-modal" :title="$t('renewService')" hide-footer no-close-on-backdrop>
      <ExtendSubscriptionModal :customer-subscription="activeSubscription" @submitted="fetchSubscriptions" />
    </b-modal>

    <b-modal id="register-subscription-modal" :title="$t('registerService')" hide-footer no-close-on-backdrop>
      <RegisterSubscriptionModal :customer-id="id" @submitted="fetchSubscriptions" />
    </b-modal>

    <b-modal id="upgrade-subscription-modal" :title="$t('upgradeService')" hide-footer no-close-on-backdrop>
      <UpgradeSubscriptionModal :customer-subscription="activeSubscription" @submitted="fetchSubscriptions" />
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
  .customer-info-text {
    font-size: 17px;
    line-height: 175%;
  }
</style>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableFieldArray} from "bootstrap-vue";

  import * as util from "@/service/util";
  import * as userService from "@/service/userService";
  import * as customerService from "@/service/customerService";
  import * as subscriptionPlanService from "@/service/subscriptionPlanService";

  import ExtendSubscriptionModal from "@/components/customer/subscription/ExtendSubscriptionModal.vue";
  import RegisterSubscriptionModal from "@/components/customer/subscription/RegisterSubscriptionModal.vue";
  import UpgradeSubscriptionModal from "@/components/customer/subscription/UpgradeSubscriptionModal.vue";


  @Component({
    components: { ExtendSubscriptionModal, RegisterSubscriptionModal, UpgradeSubscriptionModal }
  })
  export default class CustomerDetailsSubscription extends BaseComponent {

    progressBarHeight = "5px";

    id: number = null;

    isLoading = false;
    subscriptions: any[] = null;
    activeSubscriptionPlan = null;

    historyTableViewMode: "subscription" | "storage" = "subscription";
    historyTableViewModeOptions = [
      { value: "subscription", text: this.$t("serviceRegisterHistory") },
      { value: "storage", text: this.$t("storageHistory") },
    ];

    subscriptionsHistory: any[] = null;
    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#"
      },
      {
        key: "createdTime",
        label: this.$t("datetime").toString(),
        formatter: value => util.localDateFormat(value)
      },
      {
        key: "subscriptionPlanName",
        label: this.$t("subscriptionPlan").toString(),
      },
      {
        key: "numberOfConcurrentMeetings",
        label: this.$t("numberOfConcurrentMeetings").toString(),
      },
      {
        key: "startTime",
        label: this.$t("startDate").toString(),
        formatter: value => util.localDateFormat(value)
      },
      {
        key: "endTime",
        label: this.$t("expDate").toString(),
        formatter: value => util.localDateFormat(value)
      },
    ];

    storageHistory: any[] = null;
    storageHistoryTableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#"
      },
      {
        key: "appliedDate",
        label: this.$t("datetime").toString(),
        formatter: value => util.localDateFormat(value)
      },
      {
        key: "storage",
        label: this.$t("storageGb").toString(),
      },
    ];

    mounted() {
      this.id = +this.$route.params.id || null;
      this.fetchSubscriptions();
    }


    async fetchSubscriptions() {
      try {
        this.isLoading = true;
        const fetchSubscriptions = subscriptionPlanService.getCustomerSubscription(this.id);
        const fetchSubscriptionHistory = subscriptionPlanService.getCustomerSubscriptionHistoryList(this.id);
        const fetchStorageHistory = subscriptionPlanService.getCustomerStorageSubscriptionHistoryList(this.id);
        this.subscriptions = await fetchSubscriptions;
        this.subscriptionsHistory = await fetchSubscriptionHistory;
        this.storageHistory = await fetchStorageHistory;

        if (this.activeSubscription) {
          const { subscriptionPlanId } = this.activeSubscription;
          this.activeSubscriptionPlan = await subscriptionPlanService.getPackageDetail(subscriptionPlanId);
        }

      } catch (e) {
        this.toastError(e);
      } finally {
        this.isLoading = false;
      }
    }


    get activeSubscription() {
      return this.subscriptions?.find(it => it.isActive);
    }

    async onclickCancelService() {
      
      const modalMessage = this.$t("cancelServiceConfirm").toString();
      const confirmSend = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmSend) {
        try {
          this.showLoading();
          await customerService.cancelService(this.id);
          this.toastSuccessDelay(this.$t("cancelServiceSuccess").toString());
          this.fetchSubscriptions();
        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

    async onClickSendRemindEmail() {
      const modalMessage = this.$t("sendServiceRenewReminderEmailConfirm").toString();
      const confirmSend = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'primary',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmSend) {
        try {
          this.showLoading();
          await customerService.sendSubscriptionReminderEmail(this.activeSubscription.id);
          this.toastSuccessDelay(this.$t("sendServiceRenewReminderEmailSuccessful").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

  }
</script>
